import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    TimeScale,
    LinearScale,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useUser } from '../contexts/UserContext';
import styles from './PlotComponent/PlotComponent.module.css';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(LineElement, PointElement, TimeScale, LinearScale, Tooltip, Legend, Filler, zoomPlugin);

const INTERVALS = [5, 50, 500, 1000, 2000, 4000, 8000, 16000, 32000, 64000, 128000];

const DecimationLayerComponent = () => {
    const { user } = useUser();
    const [dataRows, setDataRows] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/decimation_layer_ranges`,
                    {
                        headers: { Authorization: `Bearer ${user.token}` },
                    }
                );
                setDataRows(response.data);
            } catch (error) {
                console.error('Failed to fetch decimation layer ranges:', error);
            }
        };

        fetchData();
    }, [user.token]);

    const getChartForInterval = (interval) => {
        const series = [];

        for (const row of dataRows) {
            const min = row[`min_${interval}`];
            const max = row[`max_${interval}`];

            if (min && max) {
                series.push({
                    label: `${row.source_name}.${row.channel_name}`,
                    data: [
                        { x: new Date(min), y: row.channel_id },
                        { x: new Date(max), y: row.channel_id },
                    ],
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                    tension: 0,
                });
            }
        }

        const times = [];
        const yValues = [];

        for (const row of dataRows) {
            const min = row[`min_${interval}`];
            const max = row[`max_${interval}`];
            if (min && max) {
                times.push(new Date(min));
                times.push(new Date(max));
                yValues.push(row.channel_id);
            }
        }

        const minTime = Math.min(...times.map(d => d.getTime()));
        const maxTime = Math.max(...times.map(d => d.getTime()));
        const minY = Math.min(...yValues);
        const maxY = Math.max(...yValues);

        const verticalLines = [];
        const intervalMs = interval < 2000 ? (1000 * interval * 1000) : (interval * 1000) ;

        for (let t = minTime; t <= maxTime; t += intervalMs) {
            verticalLines.push({
                label: `marker-${t}`,
                data: [
                    { x: new Date(t), y: minY },
                    { x: new Date(t), y: maxY },
                ],
                borderColor: 'rgba(255,0,0,1)',
                borderWidth: 1,
                pointRadius: 0,
                showLine: true,
                fill: false,
                tension: 0,
                borderDash: [4, 4],
            });
        }

        const chartData = {
            datasets: [...series, ...verticalLines]
        };

        const chartOptions = {
            maintainAspectRatio: false,
            responsive: true,
            scales: {
                x: {
                    type: 'time',
                    time: {
                        tooltipFormat: 'yyyy-MM-dd HH:mm',
                        displayFormats: {
                            hour: 'MMM dd, HH:mm',
                            minute: 'MMM dd, HH:mm',
                        },
                    },
                    ticks: {
                        color: '#ddd',
                    },
                    grid: {
                        color: '#555',
                    },
                    title: {
                        display: true,
                        text: 'UTC Time',
                        color: '#ddd',
                        font: { size: 14 },
                    },
                },
                y: {
                    title: {
                        display: true,
                        text: 'channel_id',
                        color: '#ddd',
                        font: { size: 14 },
                    },
                    ticks: {
                        color: '#ddd',
                    },
                    grid: {
                        color: '#555',
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        title: (tooltipItems) => tooltipItems[0].dataset.label,
                    },
                },
                zoom: {
                    pan: {
                        enabled: true,
                        mode: 'xy',
                    },
                    zoom: {
                        drag: {
                            modifierKey: 'ctrl',
                            enabled: true,
                            borderColor: 'rgba(255,255,255,0.3)',
                            borderWidth: 1,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                        mode: 'xy',
                    },
                    limits: {
                        x: { minRange: 1000 * 60 * 60 }, // Prevent zooming in too far (e.g. 1 hour)
                    },
                }
            }
        };

        return (
            <div key={interval} className={styles.plotWrapper} style={{ height: 400 }}>
                <h3 className={styles.plotTitle}>Decimation Layer: {interval} seconds</h3>
                <Line data={chartData} options={chartOptions} height={300} />
            </div>
        );
    };

    return (
        <div className={styles.allPlotsContainer}>
            {INTERVALS.map((interval) => getChartForInterval(interval))}
        </div>
    );
};

export default DecimationLayerComponent;
